import getUrlPath from '../../../utils/getUrlPath';
import logger from '../../../utils/logger';
import Send from '../../icons/Send';
import ViewOnScreen from '../../view-on-screen/ViewOnScreen';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { DEFAULT_USER_ROLE_CONTENT_PREFIX, invitationRoles } from '../../../constants/user-roles';
import { FormattedMessage, useIntl } from 'react-intl';
import { getInvitationRoles } from '../../../utils/user-roles';
import { InviteStatus } from './InviteStatus';
import { inviteTeam } from '../../../apis/signup';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { messageContents } from '../../../constants/message';
import { openMessageModal } from '../../../utils/message';
import { palette } from '../../../theme/palette';
import { RootState, useAppSelector } from '../../../redux/store';
import { url } from '../../../constants/url';
import { UserRoles } from '../../../types/user-roles';
import { useTouchedFields } from '../../../hooks/useTouchedFields';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  InviteSubmitData,
  InviteTeamMemberResponseCode,
  InviteTeamRequest,
  InvitedTeamMemberStatusModel,
  SignupUrlKeys,
} from '../../../types/signup/common';
import {
  InviteUserDetails,
  InviteUsersList,
  InviteUserItem,
  InviteUserEmail,
  InviteUserRole,
  Option,
  Separator,
  OptionLabel,
  OptionDescription,
} from '../styles/Common';
import {
  Add,
  Action,
  AddInvites,
  CogIcon,
  Form,
  FormItem,
  HelperText,
  RemoveIcon,
  InviteSubmit,
  RadioButton,
  RadioGroup,
  Label,
  InviteEmailInput,
  FormContentsWrapper,
} from '../styles/Form';
import {
  InvitedTeamMemberModel,
  InviteTeamFormModel,
  inviteTeamSchema,
} from '../../../types/signup/invite-team-form';

interface InviteFormProps {
  isMultiEntryMode?: boolean;
  showSubmitSeparator?: boolean;
  redirectAfterSubmit?: boolean;
  companyUsersContainerRef?: React.RefObject<HTMLDivElement>;
  onSubmit?: (data: InviteSubmitData) => void;
  showInviteResponse?: boolean;
  applyStyle?: string;
}

function InviteForm({
  isMultiEntryMode,
  showSubmitSeparator,
  redirectAfterSubmit,
  companyUsersContainerRef,
  onSubmit,
  showInviteResponse = true,
  applyStyle,
}: InviteFormProps) {
  const {
    control,
    getValues,
    handleSubmit,
    register,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<InviteTeamFormModel>({
    mode: 'onChange',
    resolver: zodResolver(inviteTeamSchema),
  });

  const { fields, append, remove } = useFieldArray({
    name: 'users',
    control,
  });

  const intl = useIntl();
  const authState = useAppSelector((state: RootState) => state.auth);

  const [menuMaxWidth, setMenuMaxWidth] = useState<number | undefined>(undefined);
  const [isSubmittingForm, setIsSubmittingForm] = useState<boolean>(false);
  const [touched, updateTouchedFields] = useTouchedFields();
  const [invitationSent, setInvitationSent] = useState<boolean>(false);
  const [invitationResponse, setInvitationResponse] = useState<InvitedTeamMemberStatusModel[]>([]);

  const formRef = useRef(null);

  const handleAppend = () => {
    const value = getValues();
    if (!errors.email?.message) {
      const email = value.email
        ?.split(',')
        .map((e) => e.trim())
        .filter((part) => part.length > 0);
      const param: InvitedTeamMemberModel[] | undefined = email?.map((e) => {
        return {
          email: e,
          role: UserRoles.USER,
        };
      });
      append(param ?? []);
      setValue('email', '');
    }
  };

  const navigateToRedirectPage = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectUrl = urlParams.get(SignupUrlKeys.REDIRECT_URL);
    window.location.href = redirectUrl ?? getUrlPath(url.account.id) ?? window.location.origin;
  };

  const onValidSubmit: SubmitHandler<InviteTeamFormModel> = async (data) => {
    try {
      setIsSubmittingForm(true);
      if (!authState.authContext?.companyId) {
        throw new Error('error at InviteTeam component, undefined company id');
      }

      let params: InviteTeamRequest | undefined;

      if (isMultiEntryMode) {
        params = {
          invitations: data.users.map((e) => ({
            email: e.email,
            security_level_id: invitationRoles[e.role].roleId,
          })),
        };
      } else {
        if (!data.email || !data.singleEntryUserRole) {
          throw new Error('error at InviteTeam component, undefined email id or user role');
        }
        params = {
          invitations: [
            {
              email: data.email,
              security_level_id: invitationRoles[data.singleEntryUserRole].roleId,
            },
          ],
        };
      }
      const response = await inviteTeam(authState.authContext?.companyId, params);
      const invitedEmailList = response.data
        .filter((e) => e.response_code === InviteTeamMemberResponseCode.INVITATION_SENT)
        .map((e) => e.email);

      const submitData: InviteSubmitData = {
        successfulInvites: [],
        invitationResponse: [],
      };

      if (onSubmit) {
        if (isMultiEntryMode) {
          submitData.successfulInvites = data.users.filter((e) =>
            invitedEmailList.some((email) => e.email === email),
          );
        } else if (data.email && data.singleEntryUserRole) {
          if (invitedEmailList.some((email) => data.email === email)) {
            submitData.successfulInvites = [
              {
                email: data.email,
                role: data.singleEntryUserRole,
              },
            ];
          }
        }

        if (response && response.data) {
          submitData.invitationResponse = response.data.map((res) => ({
            email: res.email,
            status: res.status,
            role:
              !data.isMultiEntryMode && data.singleEntryUserRole
                ? UserRoles[data.singleEntryUserRole]
                : data.users.filter((inv) => inv.email === res.email)[0].role,
          }));
        }

        setInvitationResponse(submitData.invitationResponse);
        setInvitationSent(true);
        onSubmit(submitData);

        if (companyUsersContainerRef?.current) {
          companyUsersContainerRef.current.scrollTo({
            top: companyUsersContainerRef.current.scrollHeight,
            behavior: 'smooth',
          });
        }
      }

      if (redirectAfterSubmit) {
        navigateToRedirectPage();
      }
    } catch (error) {
      logger('error', 'at InviteTeam component on form submit', error);
      setIsSubmittingForm(false);
      openMessageModal(messageContents.genericErrorMessage);
    }
  };

  const onInvalidSubmit = () => {
    if (!isMultiEntryMode) {
      updateTouchedFields('email');
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        handleAppend();
        break;
      default:
    }
  };

  useEffect(() => {
    if (formRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        const newWidth = entries[0].contentRect.width;
        setMenuMaxWidth(newWidth);
      });
      resizeObserver.observe(formRef.current);

      return () => resizeObserver.disconnect();
    }
  }, [formRef.current]);

  useEffect(() => {
    if (!isMultiEntryMode) {
      setValue('singleEntryUserRole', UserRoles.USER);
    }
  }, [getValues, isMultiEntryMode]);

  useEffect(() => {
    setValue('isMultiEntryMode', !!isMultiEntryMode);
  }, [isMultiEntryMode]);

  return showInviteResponse && invitationSent ? (
    <InviteStatus invitationResponseStatus={invitationResponse} />
  ) : (
    <Form onSubmit={handleSubmit(onValidSubmit, onInvalidSubmit)} ref={formRef} noValidate>
      <FormContentsWrapper applyStyle={applyStyle}>
        <AddInvites applyStyle={applyStyle}>
          <FormItem>
            <ViewOnScreen screen={{ down: 'xs' }}>
              <Label>
                <FormattedMessage id={'signup.inviteTeam.inputLabel'} />
              </Label>
            </ViewOnScreen>
            <InviteEmailInput
              {...register('email')}
              type='string'
              inputMode='email'
              hasError={
                !!(
                  (isMultiEntryMode || (!isMultiEntryMode && touched['email'])) &&
                  errors.email?.message
                )
              }
              placeholder={
                isMultiEntryMode
                  ? intl.formatMessage({ id: 'signup.inviteTeam.inputPlaceHolder' })
                  : 'bart_perez@jdsconstruction.com'
              }
              onKeyDown={handleKeyDown}
            />
            {(isMultiEntryMode || (!isMultiEntryMode && touched['email'])) &&
            errors.email?.message ? (
              <HelperText hasError>{errors.email?.message}</HelperText>
            ) : (
              isMultiEntryMode && (
                <HelperText>
                  <FormattedMessage id={'signup.inviteTeam.helperText'} />
                </HelperText>
              )
            )}
          </FormItem>
          {isMultiEntryMode && (
            <Add onClick={handleAppend} applyStyle={applyStyle}>
              <FormattedMessage id={'signup.inviteTeam.addButtonLabel'} />
            </Add>
          )}
        </AddInvites>
        {!isMultiEntryMode && (
          <FormItem>
            <RadioGroup name='singleEntryUserRole'>
              <Controller
                name='singleEntryUserRole'
                control={control}
                render={({ field }) => (
                  <>
                    {Object.values(
                      getInvitationRoles(
                        invitationRoles,
                        authState?.isOwner,
                        authState?.authContext?.securityLevelId,
                      ),
                    ).map((e) => (
                      <RadioButton
                        key={e.role}
                        value={e.role}
                        title={intl.formatMessage({
                          id: `${DEFAULT_USER_ROLE_CONTENT_PREFIX}.${e.labelAccessor}`,
                        })}
                        description={intl.formatMessage({
                          id: `${DEFAULT_USER_ROLE_CONTENT_PREFIX}.${e.descriptionAccessor}`,
                        })}
                        onChange={() => {
                          field.onChange(e.role);
                        }}
                        checked={field.value === e.role}
                      />
                    ))}
                  </>
                )}
              />
            </RadioGroup>
          </FormItem>
        )}

        {isMultiEntryMode && fields.length > 0 && (
          <>
            <ViewOnScreen screen={{ up: 'xs' }}>
              <Separator />
            </ViewOnScreen>
            <InviteUsersList>
              {fields.map((field, index) => {
                return (
                  <InviteUserItem key={index}>
                    <InviteUserDetails>
                      <InviteUserEmail>{field.email}</InviteUserEmail>
                      <Controller
                        name={`users.${index}.role`}
                        control={control}
                        render={({ field }) => (
                          <InviteUserRole
                            {...field}
                            MenuProps={{
                              anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                              transformOrigin: { vertical: 'top', horizontal: 'right' },
                              PaperProps: {
                                style: {
                                  boxShadow: 'none',
                                  border: `1px solid ${palette.custom.grey}`,
                                  padding: '5px 10px',
                                  maxWidth: menuMaxWidth ? `${menuMaxWidth}px` : 'none',
                                },
                              },
                            }}
                            renderValue={(v) => {
                              const selectedOption =
                                invitationRoles[v as keyof typeof invitationRoles];
                              return selectedOption ? (
                                <FormattedMessage
                                  id={`${DEFAULT_USER_ROLE_CONTENT_PREFIX}.${selectedOption.labelAccessor}`}
                                />
                              ) : (
                                ''
                              );
                            }}
                          >
                            {Object.values(
                              getInvitationRoles(
                                invitationRoles,
                                authState?.isOwner,
                                authState?.authContext?.securityLevelId,
                              ),
                            ).map((e) => (
                              <Option key={e.id} value={e.role}>
                                <OptionLabel>
                                  <FormattedMessage
                                    id={`${DEFAULT_USER_ROLE_CONTENT_PREFIX}.${e.labelAccessor}`}
                                  />
                                </OptionLabel>
                                <OptionDescription>
                                  <FormattedMessage
                                    id={`${DEFAULT_USER_ROLE_CONTENT_PREFIX}.${e.descriptionAccessor}`}
                                  />
                                </OptionDescription>
                              </Option>
                            ))}
                          </InviteUserRole>
                        )}
                      />
                    </InviteUserDetails>
                    <Action
                      startIcon={<RemoveIcon />}
                      onClick={() => {
                        remove(index);
                        trigger();
                      }}
                    >
                      <FormattedMessage id={'signup.inviteTeam.removeButtonLabel'} />
                    </Action>
                  </InviteUserItem>
                );
              })}
            </InviteUsersList>
          </>
        )}

        <ViewOnScreen screen={{ up: 'xs' }}>{showSubmitSeparator && <Separator />}</ViewOnScreen>
      </FormContentsWrapper>

      {(!isMultiEntryMode || (isMultiEntryMode && fields.length > 0)) && (
        <InviteSubmit
          disabled={isSubmittingForm}
          startIcon={isSubmittingForm ? <CogIcon /> : <Send />}
          type='submit'
        >
          {!isSubmittingForm && <FormattedMessage id={'signup.inviteTeam.submitButtonLabel'} />}
        </InviteSubmit>
      )}
    </Form>
  );
}

export default InviteForm;
